// src/components/LogoUploader.js - logo of the leader's group.
//
// Audit FL-43: the image URL is cache-busted once per upload (not on every render),
// the file input is reset (the same file can be chosen again), files are checked by
// extension (some Android pickers report no MIME type), the backend's Czech error is
// shown, and the limits match the backend (PNG/JPG, 10 MB).
import React, { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Upload } from 'lucide-react';
import config from '../config';
import api, { getErrorMessage } from '../services/api';
import { Alert, Button } from './ui';

const ALLOWED = ['png', 'jpg', 'jpeg', 'svg'];
const MAX_MB = 10;

const LogoUploader = ({ compact = false }) => {
  const inputRef = useRef(null);
  const [logo, setLogo] = useState(null);
  const [version, setVersion] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/dancers/group/logo')
      .then((res) => {
        if (!cancelled && res.data && res.data.logo) setLogo(res.data.logo);
      })
      .catch(() => {
        /* 404 = no logo yet; other errors are not worth a warning here */
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const upload = async (file) => {
    setIsUploading(true);
    setProgress(0);
    setError(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post('/dancers/group/logo', formData, {
        onUploadProgress: (event) => {
          if (event.total) setProgress(Math.round((event.loaded * 100) / event.total));
        },
      });
      if (response.data && response.data.logo) {
        setLogo(response.data.logo);
        setVersion(Date.now());
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Logo se nepodařilo nahrát. Zkuste to prosím znovu.'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED.includes(extension)) {
      setError('Logo musí být obrázek PNG, JPG nebo SVG.');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`Logo může mít nejvýše ${MAX_MB} MB.`);
      return;
    }
    upload(file);
  };

  const src = logo ? `${config.API_BASE_URL}/dancers/logos/${encodeURIComponent(logo)}${version ? `?v=${version}` : ''}` : null;

  return (
    <section className={compact ? '' : 'rounded-lg bg-white p-4 shadow sm:p-6'} aria-labelledby="logo-title">
      <h2 id="logo-title" className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900">
        <ImageIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
        Logo skupiny
      </h2>

      {error && (
        <Alert tone="error" className="mb-3" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center">
        <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50">
          {src ? (
            <img src={src} alt="Logo skupiny" className="max-h-full max-w-full object-contain" />
          ) : (
            <ImageIcon className="h-8 w-8 text-gray-300" aria-hidden="true" />
          )}
        </div>
        <div className="flex-1 space-y-2 text-center sm:text-left">
          {!logo && loaded && <p className="text-sm text-gray-600">Zatím nemáte nahrané logo skupiny.</p>}
          {isUploading ? (
            <div className="w-full">
              <div
                className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
                aria-label="Průběh nahrávání loga"
              >
                <div className="h-2 rounded-full bg-indigo-600" style={{ width: `${progress}%` }} />
              </div>
              <p className="mt-1 text-sm text-gray-600">Nahrávám… {progress} %</p>
            </div>
          ) : (
            <Button variant="secondary" onClick={() => inputRef.current && inputRef.current.click()}>
              <Upload className="h-4 w-4" aria-hidden="true" />
              {logo ? 'Změnit logo' : 'Nahrát logo'}
            </Button>
          )}
          <p className="text-xs text-gray-600">PNG, JPG nebo SVG, nejvýše {MAX_MB} MB (SVG 2 MB).</p>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
            onChange={handleFileChange}
            aria-label="Vybrat soubor s logem"
          />
        </div>
      </div>
    </section>
  );
};

export default LogoUploader;
